import React, { Component, ErrorInfo, ReactNode } from "react";
import { makeStyles, Text, Button, tokens } from "@fluentui/react-components";

/* global console */

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px",
    gap: "8px",
    width: "100%",
    height: "100%",
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    textAlign: "center",
  },
  message: {
    textAlign: "center",
  },
});

const DefaultFallback = ({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <Text className={styles.error}>Something went wrong</Text>
      <Text className={styles.message}>
        {error?.message || "An unexpected error occurred"}
      </Text>
      <Button appearance="primary" onClick={reset}>
        Try Again
      </Button>
    </div>
  );
};

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error:", error);
    console.error("Error Info:", errorInfo);
  }

  public reset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return <DefaultFallback error={this.state.error} reset={this.reset} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
